import { Alert } from "../alerts/alert";
import { Contact } from "@models/common/contact";
import { EntityType } from "../common/entity-type.enum";
import { ExternalEntity } from "@models/integrations/external-entity";
import { IndividualData } from "@models/individuals/individual-data";
import { Note } from "@models/notes/note";
import { Tag } from "@models/common/tag";
import { IndividualVerificationModel } from "./individual-verification.model";
import { RelationshipIndividualDto } from "./relationship-individual-dto";
import { RelationshipType } from "../relationships/relationship-type.enum";
import {
  IndividualRelationshipGroupModel
} from "@pages/individuals/components/individual-profile-page/models/individual-relationship-group-model";

export class IndividualDto extends IndividualData {
  individualId: string;
  referenceNumber?: string;
  familyGroup?: string;
  primaryContactId?: string | null;
  primaryContact?: Contact | null;
  tags: Tag[] = [];
  alerts: Alert[] = [];
  externalEntities: ExternalEntity[] = [];
  notes: Note[] = [];
  systemTags: Tag[] = [];
  contacts: Contact[] = [];
  relationshipIndividualDtoList: RelationshipIndividualDto[] = [];
  verificationInfo: IndividualVerificationModel;

  constructor(init: Partial<IndividualDto> = {}) {
    super(init);
    this.individualId = init?.individualId ?? '';
    this.primaryContactId = init?.primaryContactId;
    this.primaryContact = init?.primaryContact ? new Contact(init.primaryContact) : null;
    this.tags = init?.tags ?? [];
    this.notes = init?.notes ? init.notes.map((n) => new Note(n)) : [];
    this.systemTags = init?.systemTags ?? [];
    this.contacts = init?.contacts ? init.contacts.map((c) => new Contact(c)) : [];
    this.relationshipIndividualDtoList = init?.relationshipIndividualDtoList ? init.relationshipIndividualDtoList.map((r) => new RelationshipIndividualDto(r)) : [];
    this.alerts = init?.alerts ? init.alerts.map((a) => new Alert(a)) : [];
    this.externalEntities = init?.externalEntities ? init.externalEntities.map((e) => new ExternalEntity(e)) : [];
    this.verificationInfo = init?.verificationInfo ? new IndividualVerificationModel(init.verificationInfo) : new IndividualVerificationModel();
  }

  get companyRelationships(): RelationshipIndividualDto[] {
    return this.relationshipIndividualDtoList
      .filter((r) => r.relatedToEntityType === EntityType.Company)
      .sort((r1, r2) => r1.entityName && r2.entityName ? r1.entityName.localeCompare(r2.entityName) : 0);
  }

  get trustRelationships(): RelationshipIndividualDto[] {
    return this.relationshipIndividualDtoList
      .filter((r) => r.relatedToEntityType === EntityType.Trust)
      .sort((r1, r2) => r1.entityName && r2.entityName ? r1.entityName.localeCompare(r2.entityName) : 0);
  }

  get companyActiveRelationships(): RelationshipIndividualDto[] {
    return this.companyRelationships
      .filter((r) => !r.isResigned);
  }

  get companyRelationshipsGroups(): IndividualRelationshipGroupModel[] {
    return this.toRelationshipGroups(this.companyRelationships);
  }

  get companyActiveRelationshipsGroups(): IndividualRelationshipGroupModel[] {
    return this.toRelationshipGroups(this.companyActiveRelationships);
  }

  get trustRelationshipGroups(): IndividualRelationshipGroupModel[] {
    return this.toRelationshipGroups(this.trustRelationships);
  }

  toRelationshipGroups(relationships: RelationshipIndividualDto[]): IndividualRelationshipGroupModel[] {
    const groups: IndividualRelationshipGroupModel[] = [];

    relationships.forEach((relationship) => {
      if (relationship.type == RelationshipType.Securityholder) {
        groups.push(IndividualRelationshipGroupModel.fromRelationshipIndividualDto(relationship));
        return;
      }

      const relatedGroup = groups.find((group) => group.isRelatedRelationship(relationship));

      if (relatedGroup) {
        relatedGroup.addRelationship(relationship);
      } else {
        groups.push(IndividualRelationshipGroupModel.fromRelationshipIndividualDto(relationship));
      }
    });

    return groups;
  }
}
